import { Drawer, Form, Input, Button, Modal, Select, Space, TimePicker, Tooltip, Typography, message, Card, Spin } from 'antd';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { convertToRaw, EditorState, ContentState } from 'draft-js';
import { useEffect, useState } from 'react';
import RichTextEditor from '../Components/RichTextEditor';
import QuestionCreatorFormOption from './QuestionCreatorFormOption';
import QuizQuestionImageUpload from './QuizQuestionImageUpload';
import moment from 'moment';
import { API_URL } from "./../const.js";

const QuestionCreatorForm = ({ createQuizQuestion, handleDrawerClose, getCurrentTimestamp, setPreviewData, videoDuration, videoId, visibleForm, isAIQuestion = false }) => {
    const [form] = Form.useForm();
    const [questionTitle, setQuestionTitle] = useState('Enter a question title');
    const [questionText, setQuestionText] = useState(convertToRaw(EditorState.createEmpty().getCurrentContent()));
    const [maxDuration, setMaxDuration] = useState(moment("00:00:00", 'HH:mm:ss').seconds(videoDuration))
    const [submitLoading, setSubmitLoading] = useState(false);
    const [questionTextImageIds, setQuestionTextImageIds] = useState([]);
    const [allImages, setAllImages] = useState([]);
    const [previewImage, setPreviewImage] = useState("");
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewTitle, setPreviewTitle] = useState('');
    const [reset, setReset] = useState(false);
    const [aiQuestionFinalized, setAiQuestionFinalized] = useState(false);
    const [selectedAiQuestionType, setSelectedAiQuestionType] = useState(false);
    const [aiMode, setAiMode] = useState(false);

    const defaultFormValues = {
        title: "Enter a question title",
        text: convertToRaw(EditorState.createEmpty().getCurrentContent()),
        questionType: "select_one",
        options: [],
        timeRange: [moment('00:00:00', 'HH:mm:ss'), maxDuration]
    }
    const [initialValues, setInitialValues] = useState(defaultFormValues);

    const { Option } = Select;
    const { Paragraph, Text } = Typography;
    const { RangePicker } = TimePicker;

    const resetForm = () => {
        setQuestionTitle(defaultFormValues.title);
        setInitialValues(defaultFormValues);
        form.setFieldsValue(defaultFormValues);
    }

    const onFinish = (values) => {

        values['startVideoTimestamp'] = values['timeRange'][0].diff(moment("00:00:00", "HH:mm:ss"), 'seconds')
        values['endVideoTimestamp'] = values['timeRange'][1].diff(moment("00:00:00", "HH:mm:ss"), 'seconds')
        values['imageIds'] = questionTextImageIds;
        if (aiMode) {
            values['aiQuestion'] = true;
        }

        delete values['timeRange']
        const handleAfterSuccess = () => {
            setSubmitLoading(false);
            message.success("Quiz Question created successfully.")
            setQuestionTextImageIds([])
            setAllImages([])
            setReset(true);
            if (aiMode) {
                setAiMode(false);
                setAiQuestionFinalized(false);
                setSelectedAiQuestionType(false);
            }
            form.setFieldsValue({
                title: "Enter a question title",
                text: convertToRaw(EditorState.createEmpty().getCurrentContent()),
                questionType: "select_one",
                options: [],
                timeRange: [moment('00:00:00', 'HH:mm:ss'), maxDuration]
            })
        }
        createQuizQuestion(videoId, [values], handleAfterSuccess)
    };

    const addImageIdToQuestionImages = (id) => {
        setQuestionTextImageIds([...questionTextImageIds, id])
    }

    const addImageToAll = (imageData) => {
        setAllImages([...allImages, imageData])
    }

    const addImageIdToOptionImages = (optionIndex, id) => {
        let options = form.getFieldValue('options');
        options[optionIndex]['imageId'] = id
        form.setFieldsValue({ options })
    }

    const removeItemFromQuestionImages = (id) => {
        let newState = [...questionTextImageIds];
        let foundIndex = newState.indexOf(id);
        if (foundIndex > -1) {
            newState.splice(foundIndex, 1);
        }
        setQuestionTextImageIds(newState);

        let newAllImagesState = allImages.filter(imageData => imageData.id != id);
        setAllImages(newAllImagesState);
    }
    const handleOnSelectChange = (value) => {

        if (value == "select_one") {
            let options = form.getFieldValue('options');
            let updatedOptions = options.map(option => {
                return {
                    ...option,
                    correctAnswerFlag: false
                }
            })
            form.setFieldsValue({
                questionType: value,
                options: updatedOptions
            })
        }

        if (value == "open_ended") {
            let updatedOptions = [{
                correctAnswerFlag: true,
                optionText: convertToRaw(EditorState.createEmpty().getCurrentContent()),
                imageId: null,
                explanationText: convertToRaw(EditorState.createEmpty().getCurrentContent())
            }];
            form.setFieldsValue({
                questionType: value,
                options: updatedOptions
            })
        }
    }

    const handleOnPreviewClick = () => {
        let data = form.getFieldsValue(true);
        let options = data['options'];
        data['images'] = allImages.filter(image => questionTextImageIds.includes(image.id))
        options = options.map(option => {
            return {
                ...option,
                'image': allImages.find(image => option.imageId == image.id)
            }
        })
        data['options'] = options
        setPreviewData(data);
    }

    const handleOnQuestionTitleChange = (value) => {
        form.setFieldsValue({ title: value });
        setQuestionTitle(value)
    }

    const handleOnQuestionTextChange = (value) => {
        form.setFieldsValue({ text: value })
        setQuestionText(value)
    }

    const getDisabledHours = () => {
        let hours = [];
        for (let i = maxDuration.hours() + 1; i <= 23; i++) {
            hours.push(i);
        }
        return hours;
    }
    const setStartTime = async () => {
        let timestamp = await getCurrentTimestamp();
        let endTime = form.getFieldValue('timeRange')[1]
        let newStartTime = moment("00:00:00", 'HH:mm:ss').seconds(timestamp);
        form.setFieldsValue({
            timeRange: [newStartTime, endTime]
        })
    }

    const setEndTime = async () => {
        let timestamp = await getCurrentTimestamp();
        let startTime = form.getFieldValue('timeRange')[0];
        let newEndTime = moment("00:00:00", "HH:mm:ss").seconds(timestamp);
        form.setFieldsValue({
            timeRange: [startTime, newEndTime]
        })
    }
    const getDisabledMinutes = (selectedHour) => {
        let minutes = [];
        if (selectedHour === maxDuration.hours()) {
            for (let i = maxDuration.minutes() + 1; i <= 60; i++) {
                minutes.push(i);
            }
        }
        return minutes;
    }

    const getDisabledSeconds = (selectedHour, selectedMinute) => {
        let seconds = []
        if (selectedHour === maxDuration.hours() && selectedMinute === maxDuration.minutes()) {
            for (let i = maxDuration.seconds() + 1; i <= 60; i++) {
                seconds.push(i)
            }
        }
        return seconds
    }

    const onDrawerClose = () => {
        setAiMode(false);
        setAiQuestionFinalized(false);
        setSelectedAiQuestionType(false);
        setReset(true);
        setAllImages([]);
        setQuestionTextImageIds([]);
        handleDrawerClose();
        resetForm();
    }

    const fetchAiQuestion = (type) => {
        fetch(
            API_URL +
            `/api/v1/gpt/generate-mcq/${videoId}/${type}/`,
            {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + localStorage.getItem("access")
                }
            }
        )
            .then((response) => response.json())
            .then((result) => {
                if (result.content) {
                    const question = result.content;

                    let updatedOptions = question.options.map(o => {
                        return {
                            correctAnswerFlag: o.correctAnswerFlag,
                            optionText: convertToRaw(ContentState.createFromText(o.optionText)),
                            imageId: null,
                            explanationText: convertToRaw(ContentState.createFromText(o.explanationText))
                        }
                    });

                    const contentState = ContentState.createFromText(question.text);
                    const editorState = convertToRaw(contentState);

                    const formValues = {
                        title: question.title,
                        text: editorState,
                        questionType: question.questionType,
                        options: updatedOptions,
                        timeRange: [moment('00:00:00', 'HH:mm:ss'), maxDuration],
                    }
                    setQuestionTitle(question.title);

                    setInitialValues(formValues);
                    form.setFieldsValue(formValues);

                    setTimeout(() => {
                        setAiQuestionFinalized(true);
                    }, 100);
                }
            })
            .catch((error) => {
                console.error("Error submitting data:", error);
            });
    };

    const TitleContainer = () => {
        return (
            <>
                <div className='question-title-container' style={{ display: "flex", alignItems: "center", flexDirection: "row", justifyContent: "space-between", gap: "5px" }}>
                    <Paragraph editable={{ onChange: handleOnQuestionTitleChange, maxLength: 100 }} style={{ margin: 0, width: "100%" }}>{questionTitle}</Paragraph>
                    {aiMode && (
                        <span class="cu-gpt-badge">cuGPT</span>
                    )}
                </div>
            </>
        )
    }

    useEffect(() => {
        if (isAIQuestion) {
            setAiMode(true);
            setAiQuestionFinalized(false);
            setSelectedAiQuestionType(false);
        }
    }, [isAIQuestion]);

    return <Drawer
        title={(aiMode && !aiQuestionFinalized) ? null : <TitleContainer />}
        placement="right"
        closable={false}
        forceRender={true}
        onClose={onDrawerClose}
        visible={visibleForm}
        width={"37.5vw"}
        mask={false}
        footer={
            <div
                style={{
                    textAlign: 'right',
                }}
            >
                <Button style={{ marginRight: 8 }} onClick={onDrawerClose}>
                    Cancel
                </Button>

                <>
                    {(aiMode && !aiQuestionFinalized) ? (
                        null
                    ) : (
                        <>
                            <Button onClick={handleOnPreviewClick} style={{ marginRight: 8 }}>
                                <EyeOutlined /> Preview
                            </Button>
                            <Button
                                type="primary"
                                onClick={() => {
                                    setSubmitLoading(true)
                                    form.submit()
                                }}
                                loading={submitLoading}>
                                Create
                            </Button>
                        </>
                    )}
                </>


            </div>
        }
    >

        {(aiMode && !aiQuestionFinalized) ? (

            !selectedAiQuestionType ? (<>
                <div>
                    <h3 style={{ textAlign: "center" }}>Question Type</h3>
                    <Text type='secondary'>Select the type of question you want to generate.</Text>

                    <div className='flex flex-col gap-25 mt-10'>

                        <Card style={{ width: "100%", height: "100%" }}>
                            <div className='flex flex-col space-between gap-25 h-100'>
                                <Text type='secondary'>01. Create a question with one correct answer from multiple options.</Text>
                                <Button
                                    size={"large"}
                                    onClick={() => {
                                        setSelectedAiQuestionType(true);
                                        fetchAiQuestion(1);
                                    }}>
                                    Single Choice Question
                                </Button>
                            </div>
                        </Card>

                        <Card style={{ width: "100%", height: "100%" }}>
                            <div className='flex flex-col space-between gap-25 h-100'>
                                <Text type='secondary'>02. Create a question that allows selecting more than one correct answer.</Text>
                                <Button
                                    size={"large"}
                                    onClick={() => {
                                        setSelectedAiQuestionType(true);
                                        fetchAiQuestion(2);
                                    }}>
                                    Multiple Choice Question
                                </Button>
                            </div>
                        </Card>

                        <Card style={{ width: "100%", height: "100%" }}>
                            <div className='flex flex-col space-between gap-25 h-100'>
                                <Text type='secondary'>03. Create a question that requires a written or typed response.</Text>
                                <Button
                                    size={"large"}
                                    onClick={() => {
                                        setSelectedAiQuestionType(true);
                                        fetchAiQuestion(3);
                                    }}>
                                    Open-ended Question
                                </Button>
                            </div>
                        </Card>

                    </div>

                </div>
            </>) : (
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%"
                }}>
                    <Spin />
                    <p>Generating...</p>
                </div>
            )

        ) : (
            <Form name='questions'
                form={form}
                onFinishFailed={() => setSubmitLoading(false)}
                onFinish={onFinish}
                layout="vertical"
                initialValues={initialValues}
            >
                <RichTextEditor
                    customOnChange={handleOnQuestionTextChange}
                    editorRawData={(aiMode && aiQuestionFinalized) ? initialValues.text : undefined}
                    placeholder="Enter the question text" readOnly={false} reset={reset} setReset={setReset} />
                <br />
                <Text>Supporting Images</Text>
                <QuizQuestionImageUpload addImageToForm={addImageIdToQuestionImages} addImageToAll={addImageToAll} customOnRemove={removeItemFromQuestionImages} reset={reset} />
                <Modal
                    visible={previewVisible}
                    title={previewTitle}
                    footer={null}
                    onCancel={() => setPreviewVisible(false)}
                >
                    <img alt="example" style={{ width: '100%' }} src={previewImage} />
                </Modal>
                <br />
                <br />
                <Form.Item name="questionType" label="Question Type" rules={[{ required: true, message: 'Must select a question type.' }]}>
                    <Select
                        placeholder="Type of question"
                        onChange={handleOnSelectChange}
                    >
                        <Option value="select_one" label="Select One">Select one</Option>
                        <Option value="choose_all_that_apply" label="Choose all that apply">Choose all that apply</Option>
                        <Option value="open_ended" label="Open-ended Question">Open-ended question</Option>
                    </Select>
                </Form.Item>
                <Space align="center">
                    <Form.Item
                        name="timeRange"
                        label="Associated timestamps"
                        tooltip="Questions will be triggered when video reaches the 'End time' value you set"
                        rules={[
                            { required: true, message: 'Must select a time range.' },
                            {
                                validator: async (_, values) => {
                                    if (values[1].isBefore(values[0]) || values[1].isSame(values[0])) {
                                        return Promise.reject(new Error('End time must take place after start time.'));
                                    }
                                },
                            }]}
                    >
                        <RangePicker showNow={false} disabledHours={getDisabledHours} disabledMinutes={getDisabledMinutes} disabledSeconds={getDisabledSeconds} />
                    </Form.Item>
                    <Tooltip placement="topLeft" title="Set start time to current timestamp in video"><Button onClick={setStartTime}>Set Start Time</Button></Tooltip>
                    <Tooltip placement="topLeft" title="Set end time to current timestamp in video"><Button onClick={setEndTime}>Set End Time</Button></Tooltip>
                </Space>
                <Form.List name="options" rules={[
                    {
                        validator: async (_, options) => {
                            let data = form.getFieldsValue(true);
                            let questionType = data['questionType'];
                            if (questionType == "open_ended") {
                                if (options.length < 1) {
                                    return Promise.reject(new Error('Please enter anwer hint.'));
                                }
                            } else {
                                if (!options || options.length < 2) {
                                    return Promise.reject(new Error('At least two options required.'));
                                } else if (!options.some(option => option.correctAnswerFlag == true)) {
                                    return Promise.reject(new Error('Must have at least one correct answer.'))
                                }
                            }
                        },
                    },
                ]}>
                    {(fields, { add, remove }, { errors }) => (
                        <>
                            {fields.map((field, index) => (

                                <QuestionCreatorFormOption formRef={form} field={field} key={index} index={index} removeOption={() => remove(index)} addImageToForm={addImageIdToOptionImages} addImageToAll={addImageToAll} isOpenEndedQuestion={form.getFieldsValue(true)['questionType'] == "open_ended"} />

                            ))}

                            {form.getFieldsValue(true)['questionType'] != "open_ended" && (
                                <Form.Item>
                                    <Button type="dashed" onClick={() => {
                                        add({
                                            correctAnswerFlag: false,
                                            optionText: convertToRaw(EditorState.createEmpty().getCurrentContent()),
                                            imageId: null,
                                            explanationText: convertToRaw(EditorState.createEmpty().getCurrentContent())
                                        })
                                    }} block icon={<PlusOutlined />} >
                                        Add an option
                                    </Button>
                                    <Form.ErrorList errors={errors} />
                                </Form.Item>
                            )
                            }
                        </>
                    )}
                </Form.List>
                <Form.Item name="title" hidden >
                    <Input />
                </Form.Item>
                <Form.Item name="text" hidden >
                    <Input />
                </Form.Item>
            </Form>
        )}

    </Drawer>
}

export default QuestionCreatorForm;